/**
 * Speaking module data transformation utilities.
 *
 * Transforms backend SimulationData structure to frontend SpeakingQuestion types.
 *
 * @module speakingDataTransform
 */

import { isLiveConversationTask, type SpeakingQuestion } from '@/types/exam';
import type { SimulationData, SimulationQuestion } from '@/types/exam-simulation';

/**
 * Transform a single simulation question into a speaking question format.
 */
function transformQuestion(
  simQuestion: SimulationQuestion,
  displayId: number,
  section: number,
  contextAudio?: { url?: string | null; binary?: string | null; mimeType?: string | null } | null,
  contextImage?: { url?: string | null; binary?: string | null; mimeType?: string | null } | null,
  contextText?: string | null,
): SpeakingQuestion {
  const questionContextAudio =
    simQuestion.context?.type === 'audio'
      ? {
          url: simQuestion.context.mediaUrl || null,
          binary: simQuestion.context.binaryContent || null,
          mimeType: simQuestion.context.mimeType || null,
      }
      : null;

  const questionContextImage =
    simQuestion.context?.type === 'image'
      ? {
          url: simQuestion.context.mediaUrl || null,
          binary: simQuestion.context.binaryContent || null,
          mimeType: simQuestion.context.mimeType || null,
        }
      : null;

  const questionContextText =
    simQuestion.context?.type === 'text'
      ? simQuestion.context.content || simQuestion.context.name || null
      : null;

  const taskType = simQuestion.taskType ?? null;
  const type = simQuestion.type ?? null;

  return {
    id: simQuestion.id,
    displayId,
    prompt: simQuestion.text,
    adaptiveState: simQuestion.adaptiveState ?? 'new',
    image: simQuestion.materialUrl || null,
    contextText: contextText || questionContextText || null,
    prepTime: simQuestion.preparationTime ?? 15,
    speakTime: simQuestion.recordingDuration ?? 120,
    contextAudio: contextAudio || questionContextAudio || null,
    contextImage: contextImage || questionContextImage || null,
    section,
    type,
    taskType,
    // Resolve the conversation-task gate here (not hardcoded to a level/section):
    // a tagged question routes to the live-conversation flow (LCT-001).
    isLiveConversation: isLiveConversationTask({ type, taskType }),
  };
}

/**
 * Transform backend simulation data to frontend format for speaking module.
 *
 * Flattens section question groups into a single list of questions while
 * preserving section numbers and context audio references.
 */
export function transformSpeakingData(data: SimulationData): {
  questions: SpeakingQuestion[];
  sectionAudioMap: Record<number, { url?: string | null; binary?: string | null; mimeType?: string | null }>;
} {
  const questions: SpeakingQuestion[] = [];
  let displayId = 1;
  const sectionAudioMap: Record<number, { url?: string | null; binary?: string | null; mimeType?: string | null }> = {};

  for (const section of data.sections) {
    if (section.material_type === 'audio' && (section.material_url || (section as any).material_binary)) {
      sectionAudioMap[section.section] = {
        url: section.material_url || null,
        binary: (section as any).material_binary || null,
        mimeType: (section as any).material_mime_type || null,
      };
    }

    const groups = section.questionGroups ?? [
      {
        contextId: undefined,
        context: (section as any).context ?? null,
        questions: (section as any).questions ?? [],
      },
    ];

    for (const group of groups) {
      const contextAudio = group.context?.type === 'audio'
        ? {
            url: group.context.mediaUrl || null,
            binary: group.context.binaryContent || null,
            mimeType: group.context.mimeType || null,
          }
        : null;
      const contextImage = group.context?.type === 'image'
        ? {
            url: group.context.mediaUrl || null,
            binary: group.context.binaryContent || null,
            mimeType: group.context.mimeType || null,
          }
        : null;
      const contextText =
        group.context?.type === 'text'
          ? group.context.content || group.context.name || null
          : null;

      for (const simQuestion of group.questions) {
        const transformed = transformQuestion(
          simQuestion,
          displayId,
          section.section,
          contextAudio,
          contextImage,
          contextText,
        );
        questions.push(transformed);
        displayId += 1;
      }
    }
  }

  return { questions, sectionAudioMap };
}
